import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import { VideoRoom } from "@/components/live/video-room";
import { SharedLessonMaterials } from "@/components/live/shared-materials";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ClipboardCheck, NotebookPen, Sparkles } from "lucide-react";

export const Route = createFileRoute("/learner/live/$id")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Live · MAKAI" }] }),
  component: LearnerLiveRoom,
});

type Lesson = {
  title: string; room_name: string; status: string;
  ai_summary: string | null; ai_notes: string | null; ai_key_points: unknown;
  homework: string | null; homework_assignment_id: string | null;
  provider: string | null;
};

function LearnerLiveRoom() {
  const { id } = useParams({ from: "/learner/live/$id" });
  const { ctx } = useLearnerContext();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const attendanceId = useRef<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("live_lessons")
        .select("title, room_name, status, provider, ai_summary, ai_notes, ai_key_points, homework, homework_assignment_id")
        .eq("id", id).maybeSingle();
      if (data) setLesson(data as Lesson);
    })();
  }, [id]);

  async function onJoined() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from("live_lesson_attendance").insert({
      live_lesson_id: id, user_id: user.id, learner_id: ctx?.learnerId ?? null, role: "learner",
    }).select("id").single();
    attendanceId.current = data?.id ?? null;
  }
  async function onLeft() {
    if (!attendanceId.current) return;
    await supabase.from("live_lesson_attendance")
      .update({ left_at: new Date().toISOString() })
      .eq("id", attendanceId.current);
  }

  const displayName = ctx ? `${ctx.firstName ?? ""} ${ctx.lastName ?? ""}`.trim() || "Learner" : "Learner";
  const ended = lesson?.status === "ended";
  const keyPoints = Array.isArray(lesson?.ai_key_points) ? (lesson!.ai_key_points as string[]) : [];

  return (
    <AppShell role="learner" title={lesson?.title ?? "Live lesson"} subtitle={ended ? "Class ended — review below" : "Be respectful — your teacher can see you"}
      action={<Button asChild variant="ghost" size="sm"><Link to="/learner/live"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Link></Button>}>
      {lesson?.room_name && !ended && (
        <VideoRoom provider={lesson.provider} roomName={lesson.room_name} displayName={displayName} onJoined={onJoined} onLeft={onLeft} />
      )}

      {ended && (
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            {lesson?.ai_summary && (
              <Card className="p-4">
                <h3 className="inline-flex items-center gap-2 font-display text-lg font-bold">
                  <Sparkles className="h-5 w-5 text-primary" /> Lesson summary
                </h3>
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/90">{lesson.ai_summary}</p>
              </Card>
            )}
            {lesson?.ai_notes && (
              <Card className="p-4">
                <h3 className="inline-flex items-center gap-2 font-display text-lg font-bold">
                  <NotebookPen className="h-5 w-5 text-primary" /> Notes
                </h3>
                <pre className="mt-2 whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/90">{lesson.ai_notes}</pre>
              </Card>
            )}
            {lesson?.homework && (
              <Card className="p-4">
                <h3 className="inline-flex items-center gap-2 font-display text-lg font-bold">
                  <ClipboardCheck className="h-5 w-5 text-primary" /> Homework
                </h3>
                <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/90">{lesson.homework}</p>
                {lesson.homework_assignment_id && (
                  <Button asChild className="mt-3" size="sm">
                    <Link to="/learner/assignments">Open in Assignments</Link>
                  </Button>
                )}
              </Card>
            )}
            <SharedLessonMaterials liveLessonId={id} />
            {!lesson?.ai_summary && !lesson?.ai_notes && !lesson?.homework && (
              <Card className="p-6 text-sm text-muted-foreground">
                Your teacher hasn't published the AI wrap-up yet. Check back soon.
              </Card>
            )}

          </div>
          {keyPoints.length > 0 && (
            <Card className="p-4">
              <h3 className="mb-2 font-semibold">Key take-aways</h3>
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {keyPoints.map((k, i) => <li key={i}>{k}</li>)}
              </ul>
            </Card>
          )}
        </div>
      )}
    </AppShell>
  );
}
